import { ActivityGroupCard } from "@excellent/ui";
import { useLiveResource } from "@excellent/ui/api/live";
import { activity } from "@excellent/ui/api/contracts/activity";
import { useVerificationEnv } from "./env-provider";
import { Icon } from "./icons";

const VIEW_LABELS: Record<string, string> = {
  agents: "Agent activity",
  checks: "Checks and runs",
  workflows: "Workflow activity",
};

const SUPPORTED_VIEWS = new Set(["all", "checks", "workflows", "agents"]);

export function CompanionActivity({
  onNavigate,
  search = "",
}: {
  onNavigate: (to: string) => void;
  search?: string;
}) {
  const env = useVerificationEnv();
  const params = new URLSearchParams(search);
  const requestedView = params.get("view")?.trim().toLowerCase() ?? "all";
  const view = SUPPORTED_VIEWS.has(requestedView) ? requestedView as "all" | "checks" | "workflows" | "agents" : "all";
  const category = view === "all" ? undefined : view;
  const query = params.get("q")?.trim() ?? "";
  const { data, error } = useLiveResource(activity.list, { query: {
      env: env.environment ?? "real",
      category,
      q: query || undefined,
    },
  }, {
    topic: "activity",
    refreshInterval: 30_000,
  });
  const groups = data?.groups ?? [];
  const loading = !data && !error;
  const available = data?.scope?.available ?? true;
  const hasFilter = view !== "all" || Boolean(query);
  const scopeLabel = VIEW_LABELS[view] ?? "All activity";
  const navigateWith = (nextView: string, nextQuery = query) => {
    const next = new URLSearchParams(search);
    if (nextView === "all") next.delete("view");
    else next.set("view", nextView);
    if (nextQuery) next.set("q", nextQuery);
    else next.delete("q");
    onNavigate(`/activity${next.size ? `?${next.toString()}` : ""}`);
  };

  return (
    <main className="vp-companion-activity" data-companion-activity="live" data-route="activity">
      <header className="vp-activity-hero">
        <div className="vp-home-eyebrow">
          <span className="vp-live-dot" aria-hidden="true" />
          Live workspace
        </div>
        <h1>{loading ? "Listening for meaningful activity." : error ? "Activity is temporarily unavailable." : !available ? "Demo activity stays separate." : groups.length > 0 ? `${groups.length} meaningful change${groups.length === 1 ? "" : "s"}.` : hasFilter ? "No activity matches this view." : "Nothing needs your attention."}</h1>
        <p>
          Observations, checks, findings, decisions, and receipts appear here. Repeated low-level events are grouped so the stream stays quiet.
        </p>
      </header>

      <section className="vp-activity-stream" aria-labelledby="activity-stream-title">
        <div className="vp-activity-segments" aria-label="Activity view">
          {["all", "checks", "workflows", "agents"].map((option) => (
            <button
              key={option}
              type="button"
              data-action-id={`companion-activity.view-${option}`}
              aria-pressed={view === option}
              onClick={() => navigateWith(option)}
            >
              {option === "all" ? "All" : VIEW_LABELS[option]?.replace(" activity", "").replace(" and runs", "")}
            </button>
          ))}
        </div>
        {query ? (
          <div className="vp-activity-query">
            <span>Showing matches for <strong>{query}</strong></span>
            <button type="button" data-action-id="companion-activity.clear-search" onClick={() => navigateWith(view, "")}>Clear search</button>
          </div>
        ) : null}
        <div className="vp-home-section-heading">
          <div>
            <span>{scopeLabel}</span>
            <h2 id="activity-stream-title">What changed</h2>
          </div>
          <div className="vp-activity-scope-state">
            {view !== "all" ? <button type="button" data-action-id="companion-activity.clear-view" onClick={() => navigateWith("all")}>Clear view</button> : null}
            <small>{loading ? "Updating" : available ? "Live" : "Unavailable in Demo"}</small>
          </div>
        </div>
        {groups.length > 0 ? (
          <div className="vp-activity-groups">
            {groups.map((group) => <ActivityGroupCard key={group.key} group={group} variant="page" />)}
          </div>
        ) : (
          <div className="vp-activity-empty">
            <span><Icon name={error ? "alert" : "check"} size={19} /></span>
            <div>
              <strong>{error ? "The live stream did not load" : loading ? "Checking this node" : !available ? "Real workspace activity was not loaded" : hasFilter ? "No matching changes" : "All caught up"}</strong>
              <p>{error ? String(error instanceof Error ? error.message : error) : loading ? "Existing observations are being grouped." : !available ? "Demo activity is not recorded on this node." : hasFilter ? "Clear this view to see every recent change, or wait for matching work to arrive." : "New work will appear here when it changes a verification state or needs a decision."}</p>
            </div>
          </div>
        )}
      </section>

      <section className="vp-activity-proof" aria-label="Durable proof">
        <div>
          <span>Durable proof</span>
          <strong>The stream is a view, not the audit record.</strong>
          <p>Evidence records source and lineage metadata. Receipt records preserve reported authority, method, and result; cryptographic replay verification is unavailable.</p>
        </div>
        <div className="vp-home-text-actions">
          <button type="button" data-action-id="companion-activity.open-evidence" onClick={() => onNavigate("/evidence")}>Evidence</button>
          <button type="button" data-action-id="companion-activity.open-receipts" onClick={() => onNavigate("/receipts")}>Receipts</button>
        </div>
      </section>
    </main>
  );
}
